import { createSignal, For, Show, onMount, createEffect, onCleanup } from 'solid-js';
import { cleanFissalOutput } from '../lib/fissalCleanup.js';
import { parseSseFrames } from '../lib/fissalStream.js';
import { renderFissalMarkdown } from '../lib/renderMarkdown.jsx';
import { compressImage } from '../lib/imageCompressor.js';
import Avatar from '../components/Avatar.jsx';
import RelTime from '../components/RelTime.jsx';
import wm from '../lib/wmStore.js';
import { toast } from '../lib/toastStore.js';
import {
  fissalHttpFallback,
  fissalNetworkFallback,
  fissalEmptyFallback,
  isEmptyFissalReply,
} from '../lib/fissalFallback.js';
import './FissalOpenWebUI.css';

const ENDPOINT = '/api/fissal/open-webui/stream';

const SUGGESTIONS = [
  '💎 What is the current price trend and market floor for Dreugh Wax?',
  '📊 Summarise our top selling items and guild turnover this trade week.',
  '⚡ Check kiosk sales velocity: what items are moving fastest today?',
  '⚖️ Evaluate raw crafting materials vs refined margins right now.',
];

/**
 * Split out `<think>...</think>` tags (from deep reasoning models) into a separate
 * collapsible thought block so the main response is clean and shown by default.
 */
function splitThinkingAndResponse(text) {
  if (!text) return { thinking: '', response: '' };
  
  let thinkingParts = [];
  let remaining = text;

  // Extract all closed <think>...</think> segments
  const closedMatches = Array.from(text.matchAll(/<think>([\s\S]*?)<\/think>/gi));
  if (closedMatches.length > 0) {
    for (const match of closedMatches) {
      if (match[1]) thinkingParts.push(match[1]);
    }
    remaining = remaining.replace(/<think>[\s\S]*?<\/think>/gi, '');
  }

  // Handle in-progress unclosed <think>... stream at the end
  const openMatch = remaining.match(/<think>([\s\S]*)$/i);
  if (openMatch) {
    if (openMatch[1]) thinkingParts.push(openMatch[1]);
    remaining = remaining.replace(/<think>[\s\S]*$/i, '');
  }

  return {
    thinking: thinkingParts.join('').trim(),
    response: remaining.trim()
  };
}

function toolEmoji(name) {
  const n = String(name || '').toLowerCase();
  if (n.includes('database') || n.includes('sql')) return '🗄️';
  if (n.includes('chart') || n.includes('graph')) return '📈';
  if (n.includes('identity') || n.includes('player')) return '👤';
  if (n.includes('resolve') || n.includes('user')) return '🔍';
  if (n.includes('price') || n.includes('sales')) return '💰';
  return '⚙️';
}

function normalizeToolActivity(event) {
  const name = event?.name || event?.tool || 'Tool activity';
  const label = event?.text || event?.intent || name;
  const thought = event?.thought || '';
  const intent = event?.intent || '';
  const privileged = Boolean(event?.privileged || [
    'get_raffle_admin_status',
    'get_server_diagnostics',
    'check_service_health',
    'list_family_devices',
    'execute_owner_action',
    'get_kiosk_bid_suggestions',
  ].includes(name));

  return {
    id: `tool_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    name: String(name).replace(/[\r\n]+/g, ' '),
    label: String(label).replace(/[\r\n]+/g, ' ').slice(0, 140),
    thought: String(thought).replace(/[\r\n]+/g, ' ').slice(0, 160),
    intent: String(intent).replace(/[\r\n]+/g, ' ').slice(0, 200),
    emoji: toolEmoji(name),
    privileged,
  };
}

export default function FissalOpenWebUI(props) {
  const [messages, setMessages] = createSignal([]);
  const [input, setInput] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [toolActivity, setToolActivity] = createSignal([]);
  const [activeSessionId, setActiveSessionId] = createSignal(null);
  const [fissalAvatarUrl, setFissalAvatarUrl] = createSignal(null);
  const [showThinkingMap, setShowThinkingMap] = createSignal({});
  const [globalThinkingEnabled, setGlobalThinkingEnabled] = createSignal(true);
  const [processExpanded, setProcessExpanded] = createSignal(false);
  const [attachedImage, setAttachedImage] = createSignal(null); // { url, name, size, width, height, progress, status }
  const [compressingImage, setCompressingImage] = createSignal(false);

  // History & Session Drawer State
  const [showDrawer, setShowDrawer] = createSignal(false);
  const [drawerTab, setDrawerTab] = createSignal('sessions'); // 'sessions' | 'memories'
  const [sessions, setSessions] = createSignal([]);
  const [memories, setMemories] = createSignal([]);
  const [loadingDrawer, setLoadingDrawer] = createSignal(false);
  const [editingMemoryFact, setEditingMemoryFact] = createSignal('');

  // Session rename / delete state
  const [editingId, setEditingId] = createSignal(null);
  const [editTitle, setEditTitle] = createSignal('');
  const [deletingId, setDeletingId] = createSignal(null);

  // Scroll anchoring & Jump to Latest
  const [pinned, setPinned] = createSignal(true);
  const [hasNewBelow, setHasNewBelow] = createSignal(false);

  let inputRef;
  let fileInputRef;
  let transcriptRef;
  let msgEnd;
  let abortController = null;

  function keepMemory(msgId) {
    setMessages(current => current.map(m => {
      if (m.id === msgId && m.savedMemory) {
        return { ...m, savedMemory: { ...m.savedMemory, status: 'kept' } };
      }
      return m;
    }));
    toast.success('Memory saved to records.');
  }

  async function deleteMemory(msgId, factId) {
    try {
      await fetch(`/api/me/memories/${factId}`, { method: 'DELETE', credentials: 'same-origin' });
      setMessages(current => current.map(m => {
        if (m.id === msgId && m.savedMemory) {
          return { ...m, savedMemory: { ...m.savedMemory, status: 'deleted' } };
        }
        return m;
      }));
      toast.success('Memory removed from record.');
    } catch {
      toast.error('Failed to remove memory.');
    }
  }

  function startEditMemory(msgId, currentFact) {
    setEditingMemoryFact(currentFact);
    setMessages(current => current.map(m => {
      if (m.id === msgId && m.savedMemory) {
        return { ...m, savedMemory: { ...m.savedMemory, status: 'editing' } };
      }
      return m;
    }));
  }

  async function saveEditedMemory(msgId, factId) {
    const nextFact = editingMemoryFact().trim();
    if (!nextFact) return;
    try {
      const r = await fetch(`/api/me/memories/${factId}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fact: nextFact }),
      });
      if (r.ok) {
        setMessages(current => current.map(m => {
          if (m.id === msgId && m.savedMemory) {
            return { ...m, savedMemory: { ...m.savedMemory, fact: nextFact, status: 'kept' } };
          }
          return m;
        }));
        toast.success('Memory updated.');
      } else {
        toast.error('Failed to update memory.');
      }
    } catch {
      toast.error('Failed to update memory.');
    }
  }

  function cancelEditMemory(msgId) {
    setMessages(current => current.map(m => {
      if (m.id === msgId && m.savedMemory) {
        return { ...m, savedMemory: { ...m.savedMemory, status: 'saved' } };
      }
      return m;
    }));
  }

  function nearBottom(el) {
    if (!el) return true;
    return (el.scrollHeight - el.scrollTop - el.clientHeight) < 48;
  }

  function onTranscriptScroll() {
    if (!transcriptRef) return;
    const atBottom = nearBottom(transcriptRef);
    setPinned(atBottom);
    if (atBottom) setHasNewBelow(false);
  }

  function jumpToLatest() {
    if (msgEnd) msgEnd.scrollIntoView({ behavior: 'smooth' });
    setPinned(true);
    setHasNewBelow(false);
  }

  function toggleThinking(msgId) {
    setShowThinkingMap(prev => ({
      ...prev,
      [msgId]: !prev[msgId]
    }));
  }

  function copyMessageText(rawText) {
    const { response } = splitThinkingAndResponse(rawText);
    const toCopy = cleanFissalOutput(response || rawText || '');
    if (!toCopy) return;
    try {
      navigator.clipboard.writeText(toCopy).then(
        () => toast.success('Copied to clipboard', { ttl: 1200 }),
        () => toast.error('Copy failed')
      );
    } catch {
      toast.error('Copy failed');
    }
  }

  async function handleFileSelect(e) {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only images/screenshots can be attached');
      return;
    }
    setCompressingImage(true);
    try {
      const compressed = await compressImage(file, {
        maxDimension: 1280,
        quality: 0.82,
        onProgress: (p) => {
          setAttachedImage({
            url: null,
            name: file.name,
            status: p.message,
            progress: p.progress,
          });
        },
      });
      setAttachedImage({
        url: compressed.dataUrl,
        name: file.name,
        size: compressed.compressedSize,
        width: compressed.width,
        height: compressed.height,
        status: `Ready (${compressed.width}x${compressed.height}, ${(compressed.compressedSize / 1024).toFixed(1)} KB)`,
      });
      toast.success('Image optimized for Vision AI');
    } catch (err) {
      toast.error(`Image processing failed: ${err.message}`);
      setAttachedImage(null);
    } finally {
      setCompressingImage(false);
      if (fileInputRef) fileInputRef.value = '';
    }
  }

  async function handlePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) {
          setCompressingImage(true);
          try {
            const compressed = await compressImage(file, {
              maxDimension: 1280,
              quality: 0.82,
              onProgress: (p) => {
                setAttachedImage({
                  url: null,
                  name: 'pasted_screenshot.png',
                  status: p.message,
                  progress: p.progress,
                });
              },
            });
            setAttachedImage({
              url: compressed.dataUrl,
              name: 'pasted_screenshot.png',
              size: compressed.compressedSize,
              width: compressed.width,
              height: compressed.height,
              status: `Ready (${compressed.width}x${compressed.height}, ${(compressed.compressedSize / 1024).toFixed(1)} KB)`,
            });
            toast.success('Screenshot optimized for Vision AI', { ttl: 1200 });
          } catch (err) {
            toast.error(`Screenshot processing failed: ${err.message}`);
            setAttachedImage(null);
          } finally {
            setCompressingImage(false);
          }
        }
        break;
      }
    }
  }

  // Keep the standalone container + composer pinned to the *visual*
  // viewport on mobile. iOS Safari and Chrome on Android report a
  // layout viewport that includes the toolbar/home-indicator, so
  // `100vh` plants the composer offscreen. Tracking
  // `visualViewport.height` and `env(safe-area-inset-*)` keeps it
  // visible when the keyboard opens and the toolbar collapses.
  function applyMobileViewportFix() {
    const vp = window.visualViewport;
    const standalone = document.querySelector('.v2-standalone-view');
    if (!standalone) return;
    const vvH = vp?.height ? Math.round(vp.height) : window.innerHeight;
    // Standalone view height = visual viewport height (includes safe areas)
    standalone.style.height = `${vvH}px`;
    standalone.style.minHeight = `${vvH}px`;
    // When the keyboard is open the visual viewport shrinks — scroll
    // the transcript so the composer isn't obscured.
    if (vp && vp.height < window.innerHeight - 24) {
      transcriptRef?.scrollTo?.({ top: transcriptRef.scrollHeight, behavior: 'smooth' });
    }
  }

  // Load avatar and initial chat history on mount
  onMount(async () => {
    // Attach visualViewport listeners for mobile composer anchoring.
    // Only matters in standalone/fullscreen (fissal.ech-o.net or #fissal).
    try {
      applyMobileViewportFix();
      window.visualViewport?.addEventListener('resize', applyMobileViewportFix);
      window.visualViewport?.addEventListener('scroll', applyMobileViewportFix);
      window.addEventListener('resize', applyMobileViewportFix);
    } catch {}
    // Also watch the standalone container itself for hash-driven mounts.
    const ro = new ResizeObserver(() => applyMobileViewportFix());
    try {
      const sv = document.querySelector('.v2-standalone-view');
      if (sv) ro.observe(sv);
    } catch {}
    onCleanup(() => {
      try {
        window.visualViewport?.removeEventListener('resize', applyMobileViewportFix);
        window.visualViewport?.removeEventListener('scroll', applyMobileViewportFix);
        window.removeEventListener('resize', applyMobileViewportFix);
        ro.disconnect();
      } catch {}
    });

    try {
      const info = await fetch('/api/fissal/info', { credentials: 'same-origin' });
      if (info.ok) {
        const payload = await info.json().catch(() => ({}));
        if (payload.avatarUrl) setFissalAvatarUrl(payload.avatarUrl);
      }
    } catch {}

    try {
      const r = await fetch('/api/fissal/chat-history?limit=30', { credentials: 'same-origin' });
      if (r.ok) {
        const j = await r.json().catch(() => ({}));
        const msgs = j.messages || j.history || [];
        if (msgs.length) {
          setMessages(msgs.map(h => {
            const isAsst = h.role === 'fissal' || h.role === 'assistant';
            const rawContent = h.content || h.text || '';
            const c = isAsst ? cleanFissalOutput(rawContent) : rawContent;
            return {
              id: `hist_${h.ts || Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
              role: isAsst ? 'fissal' : 'user',
              text: isAsst && isEmptyFissalReply(c) ? fissalEmptyFallback() : c,
              rawText: rawContent,
              ts: h.ts || Date.now(),
            };
          }));
          if (msgs[msgs.length - 1]?.session_id) {
            setActiveSessionId(msgs[msgs.length - 1].session_id);
          }
        }
      }
    } catch {}
  });

  // Auto-scroll when messages change if pinned to bottom
  createEffect(() => {
    messages(); // track
    if (pinned()) {
      if (msgEnd) setTimeout(() => msgEnd.scrollIntoView({ behavior: 'smooth' }), 30);
    } else {
      setHasNewBelow(true);
    }
  });

  function useSuggestion(text) {
    setInput(text);
    setTimeout(() => inputRef?.focus(), 0);
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  }

  function startNewChat() {
    if (busy() && abortController) {
      abortController.abort();
    }
    setMessages([]);
    setInput('');
    setBusy(false);
    setToolActivity([]);
    setActiveSessionId(null);
    setShowDrawer(false);
  }

  async function openDrawer() {
    const next = !showDrawer();
    setShowDrawer(next);
    if (!next) return;
    setLoadingDrawer(true);
    try {
      const [sr, mr] = await Promise.all([
        fetch('/api/fissal/sessions?limit=30', { credentials: 'same-origin' }),
        fetch('/api/me/memories', { credentials: 'same-origin' }),
      ]);
      if (sr.ok) {
        const j = await sr.json().catch(() => ({}));
        setSessions(j.sessions || []);
      }
      if (mr.ok) {
        const j = await mr.json().catch(() => ({}));
        setMemories(j.facts || []);
      }
    } catch {} finally {
      setLoadingDrawer(false);
    }
  }

  async function loadSession(sessionId) {
    setLoadingDrawer(true);
    try {
      const r = await fetch(`/api/fissal/chat-history?session_id=${sessionId}&limit=100`, { credentials: 'same-origin' });
      if (r.ok) {
        const j = await r.json().catch(() => ({}));
        const msgs = j.messages || [];
        setMessages(msgs.map(h => {
          const isAsst = h.role === 'fissal' || h.role === 'assistant';
          const rawContent = h.content || '';
          const c = isAsst ? cleanFissalOutput(rawContent) : rawContent;
          return {
            id: `sess_${h.ts || Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            role: isAsst ? 'fissal' : 'user',
            text: isAsst && isEmptyFissalReply(c) ? fissalEmptyFallback() : c,
            rawText: rawContent,
            ts: h.ts || Date.now(),
          };
        }));
        setActiveSessionId(sessionId);
        setShowDrawer(false);
      }
    } catch {} finally {
      setLoadingDrawer(false);
    }
  }

  function startRename(e, session) {
    e.stopPropagation();
    setEditingId(session.id);
    setEditTitle(session.title || '');
  }

  async function confirmRename(e, sessionId) {
    e.stopPropagation();
    const title = editTitle().trim();
    if (!title) { setEditingId(null); return; }
    try {
      const r = await fetch(`/api/fissal/sessions/${sessionId}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });
      if (r.ok) {
        setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title } : s));
      }
    } catch {}
    setEditingId(null);
  }

  function cancelRename(e) {
    e.stopPropagation();
    setEditingId(null);
  }

  async function deleteSession(e, sessionId) {
    e.stopPropagation();
    if (deletingId() === sessionId) {
      try {
        const r = await fetch(`/api/fissal/sessions/${sessionId}`, {
          method: 'DELETE',
          credentials: 'same-origin',
        });
        if (r.ok) {
          setSessions(prev => prev.filter(s => s.id !== sessionId));
          if (activeSessionId() === sessionId) {
            startNewChat();
          }
        }
      } catch {}
      setDeletingId(null);
    } else {
      setDeletingId(sessionId);
      setTimeout(() => setDeletingId(d => d === sessionId ? null : d), 3000);
    }
  }

  async function send(event) {
    event?.preventDefault();
    const promptText = input().trim();
    const img = attachedImage();
    if ((!promptText && !img) || busy()) return;

    let fullPrompt = promptText;
    if (img?.url) {
      const imgMarkdown = `\n\n![${img.name || 'attachment'}](${img.url})`;
      fullPrompt = fullPrompt ? `${fullPrompt}${imgMarkdown}` : imgMarkdown;
    }

    setInput('');
    setAttachedImage(null);
    setToolActivity([]);
    const userMsgId = `user_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const assistantMsgId = `asst_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const assistantTs = Date.now();

    setMessages((current) => [
      ...current,
      { id: userMsgId, role: 'user', text: fullPrompt, rawText: fullPrompt, ts: Date.now() },
      { id: assistantMsgId, role: 'fissal', text: '', rawText: '', ts: assistantTs, streaming: true },
    ]);
    setBusy(true);
    setPinned(true);
    setTimeout(jumpToLatest, 0);

    const updateAssistant = (patch) => {
      setMessages((current) => current.map((message) => {
        if (message.id === assistantMsgId) {
          return { ...message, ...patch };
        }
        return message;
      }));
    };

    let accumulatedText = '';
    abortController = new AbortController();

    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({
          prompt: promptText || 'Please analyze this attached image.',
          image: img?.url ? { url: img.url, name: img.name } : undefined,
          session_id: activeSessionId(),
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const fallbackMsg = fissalHttpFallback(response.status, payload);
        throw new Error(fallbackMsg);
      }
      if (!response.body) throw new Error(fissalEmptyFallback());

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let remainder = '';
      let finalText = '';
      let completed = false;
      let streamError = null;

      const consume = (chunk, tail = '') => {
        const parsed = parseSseFrames(chunk, tail);
        for (const frame of parsed.events || []) {
          if (frame?.type === 'chunk' && typeof frame.text === 'string') {
            accumulatedText += frame.text;
            updateAssistant({ rawText: accumulatedText, text: accumulatedText });
          } else if (frame?.type === 'memory_saved') {
            updateAssistant({
              savedMemory: {
                factId: frame.fact_id,
                fact: frame.fact,
                confidence: frame.confidence,
                status: 'saved', // 'saved' | 'dismissed' | 'editing'
              }
            });
            toast.success(`Fissal noted: "${frame.fact.slice(0, 40)}${frame.fact.length > 40 ? '…' : ''}"`, { ttl: 2500 });
          } else if (frame?.type === 'tool' || frame?.type === 'tool_start' || frame?.type === 'tool_end') {
            const toolItem = normalizeToolActivity(frame);
            setToolActivity((current) => {
              // Deduplicate: skip if last entry has same name + intent
              const last = current[current.length - 1];
              if (last && last.name === toolItem.name && last.intent === toolItem.intent) {
                return current;
              }
              return [...current.slice(-5), toolItem];
            });
          } else if (frame?.type === 'done') {
            completed = true;
            if (frame.session_id) setActiveSessionId(frame.session_id);
            if (typeof frame.final_text === 'string') finalText = frame.final_text;
          } else if (frame?.type === 'error') {
            streamError = frame.code ? fissalHttpFallback(500, {}) : 'Fissal harmonic stream interrupted.';
          }
        }
        return parsed.remainder || '';
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        remainder = consume(decoder.decode(value, { stream: true }), remainder);
        if (pinned()) setTimeout(jumpToLatest, 0);
      }
      remainder = consume('', remainder);
      if (streamError) throw new Error(streamError);
      if (!completed && !accumulatedText.trim()) throw new Error(fissalNetworkFallback());

      const rawAuthoritative = finalText || accumulatedText;
      const { thinking, response: respBody } = splitThinkingAndResponse(rawAuthoritative);
      const cleanedResp = cleanFissalOutput(respBody);

      updateAssistant({
        rawText: rawAuthoritative,
        thinking,
        text: isEmptyFissalReply(cleanedResp) ? fissalEmptyFallback() : cleanedResp,
        streaming: false,
      });
    } catch (error) {
      if (error?.name === 'AbortError') {
        updateAssistant({
          text: fissalNetworkFallback(),
          streaming: false,
        });
      } else {
        updateAssistant({
          text: error?.message || fissalNetworkFallback(),
          streaming: false,
          error: true,
        });
      }
    } finally {
      setBusy(false);
      abortController = null;
      if (pinned()) setTimeout(jumpToLatest, 0);
    }
  }

  onCleanup(() => {
    if (abortController) {
      abortController.abort();
    }
  });

  const viewer = () => wm.state?.viewer || {};

  return (
    <div class="v2-widget v2-fissal-open-webui">
      {/* ── Widget Header ── */}
      <header class="v2-fow__header">
        <div class="v2-fow__brand">
          <Avatar url={fissalAvatarUrl()} name="Fissal" size={36} class="v2-fow__header-avatar" />
          <div class="v2-fow__brand-meta">
            <h2 class="v2-fow__title">Fissal Terminal</h2>
            <p class="v2-fow__subtitle">
              <span class={`v2-fow__status-dot ${busy() ? 'is-busy' : 'is-ready'}`} aria-hidden="true" />
              {busy() ? 'harmonic tone transmitting…' : 'harmonic relay active · gears aligned'}
            </p>
          </div>
        </div>

        <div class="v2-fow__header-actions">
          <button
            type="button"
            class={`v2-fow__btn-chip ${globalThinkingEnabled() ? 'is-active' : ''}`}
            title={globalThinkingEnabled() ? 'Reasoning thoughts visible' : 'Reasoning thoughts hidden'}
            onClick={() => setGlobalThinkingEnabled(v => !v)}
          >
            <span class="v2-fow__btn-label-full">{globalThinkingEnabled() ? '🧠 Thoughts: On' : '🙈 Thoughts: Off'}</span>
            <span class="v2-fow__btn-label-short">{globalThinkingEnabled() ? '🧠 On' : '🙈 Off'}</span>
          </button>
          <button
            type="button"
            class="v2-fow__btn-chip"
            title="Start a fresh conversation"
            onClick={startNewChat}
          >
            ↺ New
          </button>
          <button
            type="button"
            class={`v2-fow__btn-chip ${showDrawer() ? 'is-active' : ''}`}
            title="Session history & memories"
            onClick={openDrawer}
          >
            📁 History
          </button>
          <span class="v2-fow__mode">JONE // REAL-TIME</span>
        </div>
      </header>

      {/* ── Collapsible History & Memory Drawer ── */}
      <Show when={showDrawer()}>
        <div class="v2-fow__drawer">
          <div class="v2-fow__drawer-tabs">
            <button
              type="button"
              class={`v2-fow__drawer-tab ${drawerTab() === 'sessions' ? 'is-active' : ''}`}
              onClick={() => setDrawerTab('sessions')}
            >
              Sessions
            </button>
            <button
              type="button"
              class={`v2-fow__drawer-tab ${drawerTab() === 'memories' ? 'is-active' : ''}`}
              onClick={() => setDrawerTab('memories')}
            >
              Memories
            </button>
          </div>

          <Show when={loadingDrawer()}>
            <div class="v2-fow__drawer-loading">Loading records…</div>
          </Show>

          <Show when={!loadingDrawer()}>
            {/* Sessions List */}
            <Show when={drawerTab() === 'sessions'}>
              <Show when={sessions().length === 0}>
                <div class="v2-fow__drawer-empty">No saved sessions found.</div>
              </Show>
              <div class="v2-fow__session-list">
                <For each={sessions()}>{(s) => (
                  <div
                    class={`v2-fow__session-row ${activeSessionId() === s.id ? 'is-active' : ''}`}
                    onClick={() => editingId() === s.id ? null : loadSession(s.id)}
                  >
                    <Show when={editingId() === s.id} fallback={
                      <>
                        <span class="v2-fow__session-title" title={s.title || 'Untitled session'}>
                          {s.title || 'Untitled session'}
                        </span>
                        <span class="v2-fow__session-meta">
                          {s.message_count ? `${s.message_count} msgs · ` : ''}
                          <RelTime ts={s.updated_at} />
                        </span>
                        <div class="v2-fow__session-actions">
                          <button
                            type="button"
                            class="v2-fow__action-btn"
                            title="Rename session"
                            onClick={(e) => startRename(e, s)}
                          >
                            ✏
                          </button>
                          <button
                            type="button"
                            class={`v2-fow__action-btn ${deletingId() === s.id ? 'is-danger' : ''}`}
                            title={deletingId() === s.id ? 'Click again to confirm delete' : 'Delete session'}
                            onClick={(e) => deleteSession(e, s.id)}
                          >
                            {deletingId() === s.id ? 'Confirm?' : '🗑'}
                          </button>
                        </div>
                      </>
                    }>
                      <input
                        type="text"
                        class="v2-fow__rename-input"
                        value={editTitle()}
                        onInput={(e) => setEditTitle(e.currentTarget.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') confirmRename(e, s.id);
                          if (e.key === 'Escape') cancelRename(e);
                        }}
                        autofocus
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button type="button" class="v2-fow__action-btn" onClick={(e) => confirmRename(e, s.id)}>✓</button>
                      <button type="button" class="v2-fow__action-btn" onClick={cancelRename}>✕</button>
                    </Show>
                  </div>
                )}</For>
              </div>
            </Show>

            {/* Memories List */}
            <Show when={drawerTab() === 'memories'}>
              <Show when={memories().length === 0}>
                <div class="v2-fow__drawer-empty">No trade memories recorded yet. Fissal will learn from conversations.</div>
              </Show>
              <div class="v2-fow__memory-list">
                <For each={memories()}>{(m) => (
                  <div class="v2-fow__memory-row">
                    <span class="v2-fow__memory-text">{m.fact}</span>
                    <Show when={m.confidence}>
                      <span class="v2-fow__memory-conf">{Math.round(m.confidence * 100)}%</span>
                    </Show>
                  </div>
                )}</For>
              </div>
            </Show>
          </Show>
        </div>
      </Show>

      {/* ── Transcript Area ── */}
      <div
        class="v2-fow__transcript"
        ref={transcriptRef}
        onScroll={onTranscriptScroll}
        role="log"
        aria-live="polite"
        aria-label="Fissal terminal transcript"
      >
        <Show when={messages().length === 0}>
          <div class="v2-fow__welcome-wrapper">
            <div class="v2-fow__welcome">
              <Avatar url={fissalAvatarUrl()} name="Fissal" size={38} class="v2-fow__avatar" />
              <div>
                <strong>*soft gear hum* Fissal is listening.</strong>
                <p>Tonal relays tuned to Jone and Jode. Ask this one for market prices, guild ledger pulses, or kiosk intelligence.</p>
              </div>
            </div>
            <div class="v2-fow__suggestions" aria-label="Suggested prompts">
              <For each={SUGGESTIONS}>{(suggestion) => (
                <button type="button" class="v2-fow__suggestion" onClick={() => useSuggestion(suggestion)}>
                  <span class="v2-fow__sugg-arrow">›</span> {suggestion}
                </button>
              )}</For>
            </div>
          </div>
        </Show>

        <For each={messages()}>{(message) => {
          const parsed = () => splitThinkingAndResponse(message.rawText || message.text);
          const isFissal = message.role === 'fissal';
          const hasThinking = () => Boolean(parsed().thinking);
          const isThinkingOpen = () => Boolean(showThinkingMap()[message.id]);

          return (
            <article class={`v2-fow__message v2-fow__message--${message.role}`}>
              <div class="v2-fow__avatar-col">
                <Show when={isFissal} fallback={
                  <Avatar
                    url={viewer().avatarUrl || viewer().avatar}
                    name={viewer().displayName || viewer().name || 'You'}
                    size={28}
                    class="v2-fow__user-avatar"
                  />
                }>
                  <Avatar url={fissalAvatarUrl()} name="Fissal" size={28} class="v2-fow__msg-avatar" />
                </Show>
              </div>

              <div class="v2-fow__content-col">
                <div class="v2-fow__message-meta">
                  <span class="v2-fow__sender-name">{isFissal ? 'FISSAL' : (viewer().displayName || viewer().name || 'YOU')}</span>
                  <div class="v2-fow__meta-right">
                    <span class="v2-fow__msg-ts"><RelTime ts={message.ts} /></span>
                    <Show when={isFissal && !message.streaming}>
                      <button
                        type="button"
                        class="v2-fow__copy-btn"
                        title="Copy message"
                        onClick={() => copyMessageText(message.rawText || message.text)}
                      >
                        📋
                      </button>
                    </Show>
                  </div>
                </div>

                {/* Collapsible Reasoning Section (OpenWeb style) */}
                <Show when={isFissal && hasThinking() && globalThinkingEnabled()}>
                  <div class="v2-fow__thinking-block">
                    <button
                      type="button"
                      class="v2-fow__thinking-toggle"
                      onClick={() => toggleThinking(message.id)}
                      aria-expanded={isThinkingOpen()}
                    >
                      <span class="v2-fow__thinking-icon">{isThinkingOpen() ? '▼' : '▶'}</span>
                      <span class="v2-fow__thinking-title">🧠 Thought process</span>
                      <span class="v2-fow__thinking-badge">Reasoning</span>
                    </button>
                    <Show when={isThinkingOpen()}>
                      <div class="v2-fow__thinking-body">
                        {renderFissalMarkdown(parsed().thinking)}
                      </div>
                    </Show>
                  </div>
                </Show>

                {/* Main Response / Message Body */}
                <div class="v2-fow__message-body">
                  <Show when={isFissal} fallback={renderFissalMarkdown(message.text) || <span>{message.text}</span>}>
                    {renderFissalMarkdown(parsed().response || (!hasThinking() && message.streaming ? message.text : '')) || (
                      message.streaming
                        ? <span class="v2-fow__cursor" aria-label="Fissal is composing">▋</span>
                        : hasThinking()
                          ? <span class="v2-fow__thinking-only-hint">[Response contained only reasoning — expand thought process above to view]</span>
                          : null
                    )}
                  </Show>
                </div>

                {/* Interactive Remembered Memory Card Indicator */}
                <Show when={isFissal && message.savedMemory}>
                  <div class={`v2-fow__memory-card status-${message.savedMemory.status}`}>
                    <div class="v2-fow__memory-card-header">
                      <span class="v2-fow__memory-card-icon">🧠</span>
                      <span class="v2-fow__memory-card-title">FISSAL RECORDED A MEMORY</span>
                    </div>

                    <Show when={message.savedMemory.status === 'editing'} fallback={
                      <div class="v2-fow__memory-card-body">
                        <span class="v2-fow__memory-card-fact">"{message.savedMemory.fact}"</span>
                        <div class="v2-fow__memory-card-actions">
                          <Show when={message.savedMemory.status !== 'deleted'}>
                            <button
                              type="button"
                              class="v2-fow__mem-btn v2-fow__mem-btn--keep"
                              title="Confirm this memory is correct"
                              onClick={() => keepMemory(message.id)}
                            >
                              ✓ Correct
                            </button>
                            <button
                              type="button"
                              class="v2-fow__mem-btn v2-fow__mem-btn--edit"
                              title="Edit memory text"
                              onClick={() => startEditMemory(message.id, message.savedMemory.fact)}
                            >
                              ✏ Edit
                            </button>
                            <button
                              type="button"
                              class="v2-fow__mem-btn v2-fow__mem-btn--delete"
                              title="Forget / remove this memory"
                              onClick={() => deleteMemory(message.id, message.savedMemory.factId)}
                            >
                              ✕ Forget
                            </button>
                          </Show>
                          <Show when={message.savedMemory.status === 'deleted'}>
                            <span class="v2-fow__mem-deleted-hint">Forgotten and deleted from record.</span>
                          </Show>
                        </div>
                      </div>
                    }>
                      <div class="v2-fow__memory-card-edit-row">
                        <input
                          type="text"
                          class="v2-fow__mem-edit-input"
                          value={editingMemoryFact()}
                          onInput={(e) => setEditingMemoryFact(e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEditedMemory(message.id, message.savedMemory.factId);
                            if (e.key === 'Escape') cancelEditMemory(message.id);
                          }}
                          autofocus
                        />
                        <button
                          type="button"
                          class="v2-fow__mem-btn v2-fow__mem-btn--keep"
                          onClick={() => saveEditedMemory(message.id, message.savedMemory.factId)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          class="v2-fow__mem-btn v2-fow__mem-btn--delete"
                          onClick={() => cancelEditMemory(message.id)}
                        >
                          Cancel
                        </button>
                      </div>
                    </Show>
                  </div>
                </Show>
              </div>
            </article>
          );
        }}</For>

        {/* ── Inline Processing Indicator (aligned with message flow) ── */}
        <Show when={busy()}>
          <div class="v2-fow__message v2-fow__message--fissal v2-fow__processing" aria-label="Fissal is processing">
            <div class="v2-fow__avatar-col">
              <Avatar url={fissalAvatarUrl()} name="Fissal" size={28} class="v2-fow__msg-avatar" />
            </div>
            <div class="v2-fow__content-col v2-fow__processing-bubble">
              <div class="v2-fow__processing-gear-row">
                <span class="v2-fow__processing-gear" aria-hidden="true">⚙</span>
                <span class="v2-fow__processing-text">Processing…</span>
              </div>
              <Show when={toolActivity().length > 0}>
                <div class={`v2-fow__tools-inline ${processExpanded() ? 'is-expanded' : 'is-collapsed'}`}>
                  <button
                    type="button"
                    class="v2-fow__tools-header"
                    onClick={() => setProcessExpanded(v => !v)}
                    aria-expanded={processExpanded()}
                  >
                    <span class="v2-fow__tools-toggle">{processExpanded() ? '▼' : '▶'}</span>
                    <span class="v2-fow__tools-label">PROCESS</span>
                    <span class="v2-fow__tools-count">{toolActivity().length}</span>
                    <Show when={!processExpanded()}>
                      <span class="v2-fow__tools-last-label">{toolActivity()[toolActivity().length - 1]?.label}</span>
                    </Show>
                  </button>
                  <Show when={processExpanded()}>
                    <div class="v2-fow__tools-list">
                      <For each={toolActivity()}>{(activity) => (
                        <div
                          class={`v2-fow__tool-card ${activity.privileged ? 'is-privileged' : ''}`}
                          title={`${activity.name}${activity.privileged ? ' [PRIVILEGED AUTH]' : ''}${activity.intent ? '\nCall: ' + activity.intent : ''}${activity.thought ? '\nReturn: ' + activity.thought : ''}`}
                        >
                          <div class="v2-fow__tool-title">
                            <span class="v2-fow__tool-emoji">{activity.emoji}</span>
                            <span class="v2-fow__tool-name">{activity.label}</span>
                            <Show when={activity.privileged}>
                              <span class="v2-fow__tool-priv-badge" title="Privileged / Administrative tool authorization">🔐 PRIVILEGED</span>
                            </Show>
                          </div>
                          <Show when={activity.thought}>
                            <div class="v2-fow__tool-thought">
                              <span class="v2-fow__tool-thought-icon">💭</span>
                              <span class="v2-fow__tool-thought-text">{activity.thought}</span>
                            </div>
                          </Show>
                        </div>
                      )}</For>
                    </div>
                  </Show>
                </div>
              </Show>
            </div>
          </div>
        </Show>

        <div ref={msgEnd} style={{ height: '1px' }} />
      </div>

      {/* ── Floating Jump to Latest Pill ── */}
      <Show when={hasNewBelow() && !pinned()}>
        <button type="button" class="v2-fow__jump-pill" onClick={jumpToLatest}>
          ↓ New messages
        </button>
      </Show>

      {/* ── Composer / Input Bar ── */}
      <form class="v2-fow__composer" onSubmit={send}>
        <Show when={attachedImage()}>
          <div class="v2-fow__attachment-preview">
            <Show when={attachedImage()?.url}>
              <img src={attachedImage()?.url} alt="Attached preview" class="v2-fow__attachment-thumb" />
            </Show>
            <div class="v2-fow__attachment-info">
              <span class="v2-fow__attachment-name">{attachedImage()?.name || 'Attached screenshot'}</span>
              <span class="v2-fow__attachment-status">{attachedImage()?.status || 'Processing…'}</span>
            </div>
            <button
              type="button"
              class="v2-fow__attachment-remove"
              title="Remove attachment"
              onClick={() => setAttachedImage(null)}
            >
              ✕
            </button>
          </div>
        </Show>

        <div class="v2-fow__input-container">
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <button
            type="button"
            class="v2-fow__attach-btn"
            title="Attach image or paste screenshot from clipboard"
            onClick={() => fileInputRef?.click()}
          >
            📎
          </button>
          <span class="v2-fow__prompt-mark" aria-hidden="true">›</span>
          <textarea
            id="fissal-terminal-prompt"
            ref={inputRef}
            value={input()}
            onInput={(event) => setInput(event.currentTarget.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            class="v2-fow__textarea"
            rows="1"
            placeholder="Transmit a query to Fissal… (Paste screenshots with Ctrl+V, Shift+Enter for newline)"
            autocomplete="off"
            spellcheck={false}
            disabled={busy()}
            aria-label="Message Fissal"
          />
          <button
            type="submit"
            class="v2-fow__send"
            disabled={busy() || (!input().trim() && !attachedImage())}
            aria-label="Send prompt"
          >
            {busy() ? '…' : 'SEND ↵'}
          </button>
        </div>
      </form>
    </div>
  );
}